/**
 * CetusEd Community Portal — Message Thread View
 *
 * COMPLIANCE: Threads display the full conversation history between the
 *   parent and school staff. Each message shows the sender, timestamp,
 *   and read status. Threads are immutable — messages cannot be edited
 *   or deleted to preserve the audit trail under FERPA §1232g.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useThread, useSendMessage } from '../../api/hooks';
import { Button } from '../../ui/components/Button';
import { LoadingSpinner } from '../../ui/components/LoadingSpinner';

export const Thread: React.FC = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const { data: messages, isLoading, error } = useThread(threadId || '');
  const sendMessage = useSendMessage();
  const [replyBody, setReplyBody] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleReply = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!replyBody.trim() || !threadId) return;
      // mutate() routes failures into sendMessage.isError, which is rendered
      // below — no swallowed rejection, no invisible failure.
      sendMessage.mutate(
        {
          threadId,
          to: '',
          subject: '',
          body: replyBody.trim(),
        },
        { onSuccess: () => setReplyBody('') },
      );
    },
    [replyBody, threadId, sendMessage],
  );

  if (isLoading) return <LoadingSpinner />;

  if (error || !messages) {
    return (
      <div className="error-page">
        <h1>Thread Not Found</h1>
        <p>This conversation could not be loaded.</p>
        <Link to="/messages">Back to Messages</Link>
      </div>
    );
  }

  return (
    <div className="thread-page">
      <nav className="breadcrumb">
        <Link to="/messages">Messages</Link> /{' '}
        {messages[0]?.subject || 'Conversation'}
      </nav>

      <h1>{messages[0]?.subject || 'Conversation'}</h1>

      <div className="thread-messages">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`message-bubble message-bubble--${msg.from === 'parent' ? 'outgoing' : 'incoming'}`}
          >
            <div className="message-header">
              <span className="message-sender">{msg.fromName}</span>
              <span className="message-time">
                {new Date(msg.sentAt).toLocaleString()}
              </span>
            </div>
            <p className="message-body">{msg.body}</p>
            {msg.readAt && (
              <p className="message-read-status">Read {new Date(msg.readAt).toLocaleString()}</p>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleReply} className="reply-form">
        {sendMessage.isError && (
          <div className="form-error" role="alert">
            Your reply could not be sent. Please try again.
          </div>
        )}
        <div className="form-field">
          <label htmlFor="reply-body" className="sr-only">Reply</label>
          <textarea
            id="reply-body"
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            placeholder="Type your reply..."
            rows={3}
            maxLength={10000}
          />
        </div>
        <Button
          type="submit"
          disabled={!replyBody.trim() || sendMessage.isPending}
          isLoading={sendMessage.isPending}
        >
          Send Reply
        </Button>
      </form>
    </div>
  );
};
